import axios from "axios";

/**
 * Resolve API host:
 * - REACT_APP_BACKEND_URL=same|proxy|"" → same origin (CRA setupProxy → FastAPI)
 * - otherwise absolute URL (desktop local / prod)
 */
function resolveBackendUrl() {
  const raw = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
  if (!raw || raw === "same" || raw === "proxy") {
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "http://localhost:3000";
  }
  return raw;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const ACCESS_KEY = "matpro_token";
const REFRESH_KEY = "matpro_refresh";

export function storeAuthTokens({ access_token } = {}) {
  try {
    // Access token only — refresh stays in httpOnly cookie (never localStorage).
    if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new Event("matpass:auth-cleared"));
    } catch {
      try {
        const ev = document.createEvent("Event");
        ev.initEvent("matpass:auth-cleared", true, true);
        window.dispatchEvent(ev);
      } catch {
        /* ignore */
      }
    }
  }
}

// Attach bearer token as fallback (works even if httpOnly cookies are blocked)
api.interceptors.request.use((config) => {
  if (!config.headers?.Authorization) {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

let refreshPromise = null;

async function refreshAccessToken() {
  // Prefer httpOnly refresh cookie (withCredentials). No refresh token in JS storage.
  const { data } = await api.post(
    "/auth/refresh",
    null,
    { _skipAuthRefresh: true },
  );
  if (data?.access_token) {
    storeAuthTokens({ access_token: data.access_token });
    return data.access_token;
  }
  throw new Error("No access token");
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthRoute = /\/auth\/(login|register|refresh|forgot-password|reset-password)/.test(url);

    if (
      status === 401 &&
      !original._retry &&
      !original._skipAuthRefresh &&
      !isAuthRoute
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const token = await refreshPromise;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        clearAuthTokens();
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  let msg = "";
  if (typeof detail === "string") msg = detail;
  else if (Array.isArray(detail)) {
    msg = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  } else if (detail && typeof detail.msg === "string") {
    msg = detail.msg;
  } else {
    msg = String(detail);
  }
  // Ne jamais exposer chemins .env / noms de variables aux utilisateurs
  if (/STRIPE_|REACT_APP_|backend\/\.env|\.env|sk_test|sk_live|whsec_/i.test(msg)) {
    if (/paiement|payment|stripe|souscri/i.test(msg)) {
      return "Les paiements seront disponibles prochainement.";
    }
    return "Une erreur est survenue. Veuillez réessayer.";
  }
  return msg || "Une erreur est survenue. Veuillez réessayer.";
}

/** Messages d'auth adaptés (réseau, 401, 403, 5xx). */
export function formatAuthError(error) {
  if (!error) return "Erreur interne";
  if (!error.response) {
    // eslint-disable-next-line no-console
    console.error("[auth] serveur indisponible", error.message || error);
    return "Serveur indisponible";
  }
  const status = error.response.status;
  const detail = error.response.data?.detail;
  // eslint-disable-next-line no-console
  console.error("[auth] échec", { status, detail, url: error.config?.url });
  if (status === 401) {
    const msg = formatApiError(detail);
    if (/introuvable/i.test(msg)) return "Utilisateur introuvable";
    if (/mot de passe/i.test(msg)) return "Mot de passe incorrect";
    return msg || "Mot de passe incorrect";
  }
  if (status === 403) {
    const msg = formatApiError(detail);
    if (/admin|autoris/i.test(msg)) return "Accès administrateur refusé";
    return msg || "Accès administrateur refusé";
  }
  if (status === 429) return formatApiError(detail) || "Trop de tentatives. Réessayez plus tard.";
  if (status >= 500) return "Erreur interne";
  return formatApiError(detail) || "Erreur interne";
}
